import React from 'react';

/**
 * "Heads up — production size adjusted" modal that fires right after a
 * custom-cutline upload whose detected die-line forces a proportional
 * downscale. Purely presentational — all state lives in the parent.
 *
 * Props:
 *   info        — { requested, production } strings from the backend estimate
 *   onGoBack    — called when user clicks "Go Back & Resize"
 *   onContinue  — called when user confirms "Continue at X × Y"
 */
export const ProductionSizeModal = ({ info, onGoBack, onContinue }) => {
  if (!info) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="production-size-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg text-gray-800" data-testid="production-size-title">
              Heads up — production size adjusted
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Your artwork&rsquo;s aspect ratio doesn&rsquo;t fit a square sticker.
              We&rsquo;ve scaled it <strong>proportionally</strong> so the artwork
              isn&rsquo;t distorted.
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-lg p-4 mb-4"
          data-testid="production-size-dims"
        >
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">You requested</div>
              <div className="text-xl font-semibold text-gray-700 line-through">{info.requested}</div>
            </div>
            <div>
              <div className="text-xs text-amber-700 uppercase tracking-wide mb-1">Final production size</div>
              <div className="text-xl font-bold text-amber-800">{info.production}</div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onGoBack}
            className="flex-1 py-3 border-2 border-[#4A7C59] text-[#4A7C59] rounded-lg font-semibold hover:bg-green-50 transition-colors"
            data-testid="production-size-back-btn"
          >
            Go Back & Resize
          </button>
          <button
            onClick={onContinue}
            className="flex-1 py-3 bg-[#4A7C59] text-white rounded-lg font-semibold hover:bg-[#3d6849] transition-colors"
            data-testid="production-size-continue-btn"
          >
            Continue at {info.production}
          </button>
        </div>
      </div>
    </div>
  );
};
